import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
    Eye, 
    Download, 
    X, 
    ExternalLink, 
    FileText, 
    Loader2,
    Maximize2,
    ZoomIn,
    ZoomOut
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Composant de prévisualisation PDF
 * Affiche un PDF dans un modal avec options de zoom et téléchargement
 */
export function PDFPreviewModal({ 
    isOpen, 
    onClose, 
    filePath, 
    fileName,
    title = "Aperçu du document"
}) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [zoom, setZoom] = useState(100);

    // Construire l'URL du fichier
    const getFileUrl = () => {
        if (!filePath) return null;
        
        // Si le chemin commence par /app/uploads, le convertir en URL API
        if (filePath.startsWith('/app/uploads/')) {
            const relativePath = filePath.replace('/app/uploads/', '');
            return `${API_URL}/api/uploads/${relativePath}`;
        }
        
        // Si c'est déjà une URL complète
        if (filePath.startsWith('http')) {
            return filePath;
        }
        
        // Sinon, ajouter le préfixe API
        return `${API_URL}/api/uploads/${filePath}`;
    };

    const fileUrl = getFileUrl();

    const handleDownload = () => {
        if (fileUrl) {
            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = fileName || 'document.pdf';
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleOpenNewTab = () => {
        if (fileUrl) {
            window.open(fileUrl, '_blank');
        }
    };

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev + 25, 200));
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev - 25, 50));
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0">
                <DialogHeader className="px-4 py-3 border-b bg-slate-50 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <DialogTitle className="flex items-center gap-2 text-lg">
                            <FileText className="w-5 h-5 text-blue-500" />
                            {title}
                        </DialogTitle>
                        <div className="flex items-center gap-2">
                            {/* Contrôles de zoom */}
                            <div className="flex items-center gap-1 bg-white rounded-lg border px-2 py-1">
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={handleZoomOut}
                                    disabled={zoom <= 50}
                                    className="h-7 w-7 p-0"
                                >
                                    <ZoomOut className="w-4 h-4" />
                                </Button>
                                <span className="text-sm font-medium min-w-[3rem] text-center">{zoom}%</span>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={handleZoomIn}
                                    disabled={zoom >= 200}
                                    className="h-7 w-7 p-0"
                                >
                                    <ZoomIn className="w-4 h-4" />
                                </Button>
                            </div>
                            
                            {/* Actions */}
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={handleOpenNewTab}
                                className="gap-1"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Nouvel onglet
                            </Button>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={handleDownload}
                                className="gap-1"
                            >
                                <Download className="w-4 h-4" />
                                Télécharger
                            </Button>
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={onClose}
                                className="h-8 w-8 p-0"
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                    {fileName && (
                        <p className="text-sm text-slate-500 mt-1">{fileName}</p>
                    )}
                </DialogHeader>
                
                <div className="flex-1 overflow-auto bg-slate-200 p-4">
                    {loading && !error && (
                        <div className="absolute inset-0 flex items-center justify-center bg-slate-100/80 z-10">
                            <div className="flex flex-col items-center gap-2">
                                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                                <span className="text-sm text-slate-600">Chargement du document...</span>
                            </div>
                        </div>
                    )}
                    
                    {error ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                            <FileText className="w-16 h-16 text-slate-300 mb-4" />
                            <h3 className="font-semibold text-slate-700">Impossible d'afficher le document</h3>
                            <p className="text-sm text-slate-500 mt-1 mb-4">
                                Le fichier n'a pas pu être chargé dans la prévisualisation.
                            </p>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={handleOpenNewTab}>
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    Ouvrir dans un nouvel onglet
                                </Button>
                                <Button onClick={handleDownload}>
                                    <Download className="w-4 h-4 mr-2" />
                                    Télécharger
                                </Button>
                            </div>
                        </div>
                    ) : fileUrl ? (
                        <div 
                            className="flex justify-center"
                            style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top center' }}
                        >
                            <iframe
                                src={`${fileUrl}#toolbar=0&navpanes=0`}
                                className="w-full bg-white shadow-lg rounded"
                                style={{ 
                                    height: `${Math.max(800, 800 * (100 / zoom))}px`,
                                    maxWidth: '900px'
                                }}
                                onLoad={() => setLoading(false)}
                                onError={() => {
                                    setLoading(false);
                                    setError(true);
                                }}
                                title={fileName || 'Document PDF'}
                            />
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full">
                            <FileText className="w-16 h-16 text-slate-300 mb-4" />
                            <p className="text-slate-500">Aucun fichier à afficher</p>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Bouton de prévisualisation PDF
 * Petit bouton pour ouvrir la prévisualisation
 */
export function PDFPreviewButton({ 
    filePath, 
    fileName, 
    title,
    variant = "outline",
    size = "sm",
    className = ""
}) {
    const [isOpen, setIsOpen] = useState(false);

    if (!filePath) return null;

    return (
        <>
            <Button 
                variant={variant} 
                size={size}
                onClick={() => setIsOpen(true)}
                className={`gap-1 ${className}`}
                data-testid="pdf-preview-btn"
            >
                <Eye className="w-4 h-4" />
                Aperçu
            </Button>
            
            <PDFPreviewModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                filePath={filePath}
                fileName={fileName}
                title={title}
            />
        </>
    );
}

export default PDFPreviewModal;
